import type { SyncProvider } from './syncProvider.js';
import {
  SCHEMA_VERSION, REMOTE_STORAGE_PREFIX, REMOTE_MODULES_PREFIX, REMOTE_ROOT_PREFIX,
  type RemoteFile, type StorageEntry, type SyncedStorage, type SyncedModule,
  type SyncResult, emptyResult,
} from './types.js';
import { syncState } from './syncState.js';
import { storage } from '../shell/storage.js';
import { getOrCreateDeviceId } from '../shell/deviceId.js';
import type { ModuleRecord } from '../shell/types.js';
import { getSharedBindings, saveSharedBindings, syncSharedModule } from './sharedSync.js';

/**
 * Port of Kotlin SyncEngine. Same reconciliation rules:
 *   - Per-module DATA  → per-key LWW on the .sync_meta timestamps
 *   - Per-module CODE  → whole-module LWW on lastModifiedMs (the timestamp
 *     of the most recent htmlSource change).
 *
 * Code sync also auto-discovers: any module file in shell/modules/ that
 * isn't installed locally gets pulled and installed with sync modes on,
 * so creating a module on Android makes it show up on web automatically.
 *
 * Deletion handling for data exactly mirrors Kotlin (no tombstones), using
 * `lastDataSyncMs` on each ModuleRecord to disambiguate adds from deletes.
 */

interface DataOutcome {
  uploaded: number;
  downloaded: number;
  deleted: number;
  pushed: boolean;
}

/** How many remote files to fetch at once during the prefetch phase. */
const SYNC_CONCURRENCY = 6;

// ── Skip-read version cache ─────────────────────────────────────────────────
// Per (type, module) we store "<driveModifiedMs>|<localSignature>" from the
// last reconcile in localStorage; next sync skips the read iff both still match.
const SKIP_CACHE_KEY = 'shell.sync.skipVersions';

function loadSkipCache(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(SKIP_CACHE_KEY) || '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

function saveSkipCache(cache: Record<string, string>): void {
  try { localStorage.setItem(SKIP_CACHE_KEY, JSON.stringify(cache)); } catch { /* quota */ }
}

/**
 * Cheap fingerprint of a module's local DATA: count + newest timestamp over the
 * keys that actually sync (mirrors collectLocalKeys' filter). A save bumps the
 * newest ts; a delete shrinks the count — so any change moves the signature.
 * Clock-independent: uses our own per-key meta timestamps.
 */
function localDataSignal(moduleId: string): string {
  const meta = storage.getMeta(moduleId);
  let count = 0;
  let max = 0;
  for (const [k, ts] of Object.entries(meta)) {
    if (k.startsWith('__published__::') || k.startsWith('.')) continue;
    count++;
    if (ts > max) max = ts;
  }
  return `${count}:${max}`;
}

/**
 * Read every path in parallel with a bounded worker pool, returning a
 * path → content map (null for a missing/failed read, matching readFile).
 * The reads are the bottleneck of a sync and are independent, so this turns
 * a serial walk into a handful of batches without flooding Drive.
 */
async function fetchAllParallel(
  provider: SyncProvider,
  paths: string[],
  limit: number,
): Promise<Map<string, string | null>> {
  const out = new Map<string, string | null>();
  let next = 0;
  async function worker(): Promise<void> {
    while (next < paths.length) {
      const path = paths[next++]!;
      out.set(path, await provider.readFile(path));
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, paths.length) }, () => worker()),
  );
  return out;
}

export async function syncNow(provider: SyncProvider | null): Promise<SyncResult> {
  syncState.markStarted();
  // Reconcile against what modules ACTUALLY persisted, not a stale launcher
  // cache: module iframes / warm-up frames write through their own
  // StorageCache over the shared IndexedDB. Without this refresh the launcher
  // can miss (or even phantom-delete) data edited inside a module. See
  // storage.refreshFromDb().
  await storage.refreshFromDb();
  const result = emptyResult();

  if (!provider) {
    result.errors.push('No sync provider selected');
    syncState.setNotConfigured();
    return result;
  }
  if (!provider.isAuthenticated()) {
    result.errors.push('Sync provider not authenticated — connect first');
    syncState.markError(result.errors[0]!);
    return result;
  }

  // Cheap upfront listing of everything in the shell/ root.
  let remoteFiles: Map<string, RemoteFile>;
  try {
    const list = await provider.listFiles(REMOTE_ROOT_PREFIX);
    remoteFiles = new Map(list.map((f) => [f.path, f]));
  } catch (e) {
    console.error('[syncEngine] listFiles failed', e);
    result.errors.push(`List failed: ${(e as Error).message}`);
    syncState.markError(result.errors[0]!);
    return result;
  }

  const now = Date.now();
  const deviceId = getOrCreateDeviceId();

  // Decide what actually needs reading. We skip a file when BOTH its Drive
  // modifiedMs (exact equality, server clock) AND a cheap local signature are
  // unchanged since we last reconciled it — i.e. nothing changed on either
  // side. Any change breaks an equality, so a skip can never miss an update.
  const modulesList = storage.listModules() as Array<[string, ModuleRecord]>;
  const skipCache = loadSkipCache();
  const skipData = new Set<string>();
  const skipCode = new Set<string>();
  const pathsToFetch: string[] = [];
  for (const [id, rec] of modulesList) {
    if (rec.dataSyncMode === 'sync') {
      const p = `${REMOTE_STORAGE_PREFIX}/${id}.json`;
      const rf = remoteFiles.get(p);
      if (rf) {
        const sig = `${rf.remoteModifiedMs}|${localDataSignal(id)}`;
        if (skipCache[`d:${id}`] === sig) skipData.add(id); else pathsToFetch.push(p);
      }
    }
    if (rec.codeSyncMode === 'sync' && !id.startsWith('__')) {
      const p = `${REMOTE_MODULES_PREFIX}/${id}.json`;
      const rf = remoteFiles.get(p);
      if (rf) {
        const sig = `${rf.remoteModifiedMs}|${rec.lastModifiedMs}`;
        if (skipCache[`c:${id}`] === sig) skipCode.add(id); else pathsToFetch.push(p);
      }
    }
  }
  const remoteContent = await fetchAllParallel(provider, pathsToFetch, SYNC_CONCURRENCY);

  // ─── DATA sync (existing local modules opted in) ─────────────────────
  for (const [id, rec] of modulesList) {
    if (rec.dataSyncMode !== 'sync') continue;
    if (skipData.has(id)) continue;
    const lastDataSync = rec.lastDataSyncMs ?? 0;
    try {
      const outcome = await syncModuleData(
        provider, id, lastDataSync, remoteFiles, remoteContent, deviceId,
      );
      result.dataUploaded += outcome.uploaded;
      result.dataDownloaded += outcome.downloaded;
      result.dataDeleted += outcome.deleted;
      if (outcome.pushed || outcome.downloaded > 0 || outcome.deleted > 0) {
        // Stamp the data sync watermark — keep code's lastModifiedMs untouched.
        const cur = storage.getModule(id) as ModuleRecord | undefined;
        if (cur) storage.putModule(id, { ...cur, lastDataSyncMs: now });
      }
      // Refresh the skip cache (invalidate after a push — remote modifiedMs
      // changed and is unknown until the next listing).
      const rf = remoteFiles.get(`${REMOTE_STORAGE_PREFIX}/${id}.json`);
      if (outcome.pushed || !rf) delete skipCache[`d:${id}`];
      else skipCache[`d:${id}`] = `${rf.remoteModifiedMs}|${localDataSignal(id)}`;
    } catch (e) {
      console.error(`[syncEngine] data sync failed for ${id}`, e);
      result.errors.push(`Data sync ${id}: ${(e as Error).message}`);
    }
  }

  // ─── CODE sync: auto-discover new remote modules ──────────────────────
  // Any shell/modules/<id>.json not present locally → install with sync on,
  // so a module created on Android shows up here automatically.
  for (const [path] of remoteFiles) {
    if (!path.startsWith(`${REMOTE_MODULES_PREFIX}/`)) continue;
    const fileName = path.slice(REMOTE_MODULES_PREFIX.length + 1);
    if (!fileName.endsWith('.json')) continue;
    const moduleId = fileName.slice(0, -5);
    // Skip internals (e.g. __launcher__) and empty IDs
    if (!moduleId || moduleId.startsWith('__')) continue;
    if (storage.getModule(moduleId)) continue;
    try {
      const json = await provider.readFile(path);
      if (!json) continue;
      const snap = safeParseModuleSnap(json);
      if (!snap) continue;
      // Tombstones are delete markers, not installable modules.
      if (snap.deleted) continue;
      const html = snap.files['index.html'];
      if (!html) {
        console.warn(`[syncEngine] discovered module ${moduleId} has no index.html, skipping`);
        continue;
      }
      const cfg = moduleConfigFromFiles(snap.files);
      storage.putModule(moduleId, {
        id: moduleId,
        name: cfg.label ?? prettyName(moduleId),
        icon: cfg.icon,
        htmlSource: html,
        // Preserve the full file map — protects Android's revisions/
        // and module.json from being stripped on a future web push.
        files: { ...snap.files },
        lastModifiedMs: snap.lastModifiedMs,
        lastDataSyncMs: 0,
        lastCodeSyncMs: now,
        dataSyncMode: 'sync',
        codeSyncMode: 'sync',
      } satisfies ModuleRecord);
      result.codeDownloaded++;
    } catch (e) {
      console.error(`[syncEngine] discover ${moduleId} failed`, e);
      result.errors.push(`Discover ${moduleId}: ${(e as Error).message}`);
    }
  }

  // ─── CODE sync (existing local modules opted in) ──────────────────────
  for (const [id, rec] of modulesList) {
    if (rec.codeSyncMode !== 'sync') continue;
    if (id.startsWith('__')) continue;
    if (skipCode.has(id)) continue;
    try {
      const outcome = await syncModuleCode(provider, id, rec, remoteFiles, remoteContent, deviceId);
      if (outcome === 'uploaded') result.codeUploaded++;
      else if (outcome === 'downloaded') result.codeDownloaded++;
      else if (outcome === 'deleted') result.codeDeleted++;
      // Stamp lastCodeSyncMs (informational — deletion now propagates via
      // explicit tombstones, not via this watermark).
      if (outcome !== 'deleted') {
        const cur = storage.getModule(id) as ModuleRecord | undefined;
        if (cur) storage.putModule(id, { ...cur, lastCodeSyncMs: now });
      }
      // Refresh the skip cache against the CURRENT record (a pull bumps
      // lastModifiedMs). Invalidate after an upload/delete.
      const rf = remoteFiles.get(`${REMOTE_MODULES_PREFIX}/${id}.json`);
      const cur = storage.getModule(id) as ModuleRecord | undefined;
      if (outcome === 'uploaded' || outcome === 'deleted' || !rf || !cur) {
        delete skipCache[`c:${id}`];
        if (outcome === 'deleted') delete skipCache[`d:${id}`];
      } else {
        skipCache[`c:${id}`] = `${rf.remoteModifiedMs}|${cur.lastModifiedMs}`;
      }
    } catch (e) {
      console.error(`[syncEngine] code sync failed for ${id}`, e);
      result.errors.push(`Code sync ${id}: ${(e as Error).message}`);
    }
  }

  // ─── Shared-folder pass (F2 multi-user data sharing) ──────────────────
  // Runs after the private sync so pulled shared records are already the
  // freshest local state. Per-binding failures don't fail the sync run.
  const sharedBindings = getSharedBindings();
  const sharedIds = Object.keys(sharedBindings);
  if (sharedIds.length > 0 && provider.hasSharedScope?.()) {
    let bindingsDirty = false;
    for (const moduleId of sharedIds) {
      const binding = sharedBindings[moduleId]!;
      try {
        const outcome = await syncSharedModule(provider, moduleId, binding, skipCache, deviceId);
        result.dataUploaded += outcome.uploaded;
        result.dataDownloaded += outcome.downloaded;
        result.dataDeleted += outcome.deleted;
        bindingsDirty = true;
      } catch (e) {
        console.error(`[syncEngine] shared sync failed for ${moduleId}`, e);
        result.errors.push(`Shared sync ${moduleId}: ${(e as Error).message}`);
      }
    }
    if (bindingsDirty) saveSharedBindings(sharedBindings);
  }

  saveSkipCache(skipCache);
  result.completedAtMs = now;
  result.success = result.errors.length === 0;
  syncState.markFinished(result);
  await storage.flush();
  return result;
}

function prettyName(id: string): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

/**
 * Android keeps a module's label/icon/section in module.json inside the
 * module dir, which rides along in the synced files map. Honouring it here
 * is what makes names and icons match across devices.
 */
function moduleConfigFromFiles(files: Record<string, string>): { label?: string; icon?: string } {
  const raw = files['module.json'];
  if (!raw) return {};
  try {
    const cfg = JSON.parse(raw) as { label?: unknown; icon?: unknown };
    return {
      label: typeof cfg.label === 'string' ? cfg.label : undefined,
      icon: typeof cfg.icon === 'string' ? cfg.icon : undefined,
    };
  } catch {
    return {};
  }
}

// ─── Per-module DATA sync ────────────────────────────────────────────────────

async function syncModuleData(
  provider: SyncProvider,
  moduleId: string,
  lastSyncMs: number,
  remoteFiles: Map<string, RemoteFile>,
  remoteContent: Map<string, string | null>,
  deviceId: string,
): Promise<DataOutcome> {
  const remotePath = `${REMOTE_STORAGE_PREFIX}/${moduleId}.json`;

  const localMeta = storage.getMeta(moduleId);
  const localKeys = collectLocalKeys(moduleId, localMeta);

  const remoteJson = remoteFiles.has(remotePath)
    ? (remoteContent.get(remotePath) ?? null)
    : null;
  const remoteSnap = remoteJson ? safeParseStorageSnap(remoteJson) : null;

  // First sync ever — just push.
  if (!remoteSnap) {
    const snap: SyncedStorage = {
      schemaVersion: SCHEMA_VERSION,
      moduleId,
      lastModifiedMs: Date.now(),
      deviceId,
      keys: localKeys,
    };
    const ok = await provider.writeFile(remotePath, JSON.stringify(snap));
    return {
      uploaded: ok ? Object.keys(localKeys).length : 0,
      downloaded: 0, deleted: 0, pushed: ok,
    };
  }

  // ── Merge ────────────────────────────────────────────────────────────
  const merged: Record<string, StorageEntry> = {};
  const toWriteLocal: Record<string, StorageEntry> = {};
  const toDeleteLocal: string[] = [];
  let uploaded = 0;
  let downloaded = 0;
  let deleted = 0;

  const allKeys = new Set<string>([...Object.keys(localKeys), ...Object.keys(remoteSnap.keys)]);
  for (const key of allKeys) {
    const l = localKeys[key];
    const r = remoteSnap.keys[key];

    if (l && r) {
      if (l.ts > r.ts) { merged[key] = l; uploaded++; }
      else if (r.ts > l.ts) { merged[key] = r; toWriteLocal[key] = r; downloaded++; }
      else merged[key] = l;
    } else if (!l && r) {
      if (r.ts >= lastSyncMs) {
        merged[key] = r;
        toWriteLocal[key] = r;
        downloaded++;
      } else {
        // Deleted locally after last sync — propagate
        deleted++;
      }
    } else if (l && !r) {
      if (l.ts >= lastSyncMs) {
        merged[key] = l;
        uploaded++;
      } else {
        // Deleted remotely after last sync — apply locally
        toDeleteLocal.push(key);
        deleted++;
      }
    }
  }

  // Apply local-side changes via storage.
  for (const [key, entry] of Object.entries(toWriteLocal)) {
    storage.set({ moduleId, key }, entry.value);
    // storage.set bumped the meta to Date.now() — overwrite with the
    // authoritative remote timestamp so the next sync compares correctly.
    storage.setMetaTimestamp(moduleId, key, entry.ts);
  }
  for (const key of toDeleteLocal) {
    storage.delete({ moduleId, key });
  }

  const needsPush =
    uploaded > 0 || deleted > 0 ||
    !sameKeys(merged, remoteSnap.keys);

  let pushed = false;
  if (needsPush) {
    const snap: SyncedStorage = {
      schemaVersion: SCHEMA_VERSION,
      moduleId,
      lastModifiedMs: Date.now(),
      deviceId,
      keys: merged,
    };
    pushed = await provider.writeFile(remotePath, JSON.stringify(snap));
  }

  return { uploaded, downloaded, deleted, pushed };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function collectLocalKeys(moduleId: string, meta: Record<string, number>): Record<string, StorageEntry> {
  // Walk every kv entry for this module, excluding internal namespaces
  // (__published__::, .sync_meta-style internals). Mirrors the Kotlin
  // "non-dotfile *.json" filter.
  const out: Record<string, StorageEntry> = {};
  for (const key of storage.listKeysForModule(moduleId)) {
    if (key.startsWith('__published__::') || key.startsWith('.')) continue;
    const value = storage.get({ moduleId, key });
    const ts = meta[key] ?? Date.now();
    out[key] = { value, ts };
  }
  return out;
}

function sameKeys(a: Record<string, StorageEntry>, b: Record<string, StorageEntry>): boolean {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  for (const k of ak) {
    const av = a[k]; const bv = b[k];
    if (!bv || av!.value !== bv.value || av!.ts !== bv.ts) return false;
  }
  return true;
}

function safeParseStorageSnap(json: string): SyncedStorage | null {
  try {
    const obj = JSON.parse(json) as SyncedStorage;
    if (!obj || typeof obj !== 'object' || !obj.keys) return null;
    return obj;
  } catch (e) {
    console.warn('[syncEngine] failed to parse storage snapshot', e);
    return null;
  }
}

/**
 * Removes a module's snapshot files from Drive — for SYNC-OFF toggles.
 * Since deletion went tombstone-based, a missing file is harmless ("nothing
 * to sync"); real deletions go through markModuleDeletedOnDrive instead.
 * Fire-and-forget: returns true/false but never throws.
 */
export async function cleanupModuleFromDrive(
  provider: SyncProvider | null,
  id: string,
  deleteData: boolean,
  deleteCode: boolean,
): Promise<boolean> {
  if (!provider || !provider.isAuthenticated()) return false;
  let allOk = true;
  try {
    if (deleteData) {
      const ok = await provider.deleteFile(`${REMOTE_STORAGE_PREFIX}/${id}.json`);
      if (!ok) allOk = false;
    }
    if (deleteCode) {
      const ok = await provider.deleteFile(`${REMOTE_MODULES_PREFIX}/${id}.json`);
      if (!ok) allOk = false;
    }
  } catch (e) {
    console.warn(`[syncEngine] cleanupModuleFromDrive(${id}) failed`, e);
    allOk = false;
  }
  return allOk;
}

/**
 * Marks a module as DELETED on Drive. Writes a tombstone ({deleted:true})
 * to shell/modules/<id>.json instead of removing the file — the explicit
 * signal other devices act on — and drops the data snapshot.
 *
 * Contrast with cleanupModuleFromDrive (sync turned OFF): that deletes the
 * files outright, which since the tombstone change means "nothing to sync"
 * rather than "delete everywhere".
 */
export async function markModuleDeletedOnDrive(
  provider: SyncProvider | null,
  id: string,
  tombstoneCode: boolean,
  deleteData: boolean,
): Promise<boolean> {
  if (!provider || !provider.isAuthenticated()) return false;
  let allOk = true;
  try {
    if (deleteData) {
      const ok = await provider.deleteFile(`${REMOTE_STORAGE_PREFIX}/${id}.json`);
      if (!ok) allOk = false;
    }
    if (tombstoneCode) {
      const now = Date.now();
      const tomb: SyncedModule = {
        schemaVersion: SCHEMA_VERSION,
        moduleId: id,
        lastModifiedMs: now,
        deviceId: getOrCreateDeviceId(),
        files: {},
        deleted: true,
        deletedAtMs: now,
      };
      const ok = await provider.writeFile(`${REMOTE_MODULES_PREFIX}/${id}.json`, JSON.stringify(tomb));
      if (!ok) allOk = false;
    }
  } catch (e) {
    console.warn(`[syncEngine] markModuleDeletedOnDrive(${id}) failed`, e);
    allOk = false;
  }
  return allOk;
}

function safeParseModuleSnap(json: string): SyncedModule | null {
  try {
    const obj = JSON.parse(json) as SyncedModule;
    if (!obj || typeof obj !== 'object') return null;
    // Tombstones may omit the files map; normalize so callers can rely on it.
    if (obj.deleted) return { ...obj, files: obj.files ?? {} };
    if (!obj.files) return null;
    return obj;
  } catch (e) {
    console.warn('[syncEngine] failed to parse module snapshot', e);
    return null;
  }
}

// ─── Per-module CODE sync ────────────────────────────────────────────────────

type CodeOutcome = 'uploaded' | 'downloaded' | 'up_to_date' | 'deleted';

/**
 * Whole-module last-write-wins on lastModifiedMs (matches Kotlin SyncEngine).
 * On web, modules are single-file (index.html only) — multi-file modules
 * from Android still work; we use files["index.html"] and ignore the rest.
 *
 * Deletion propagation uses explicit TOMBSTONES ({deleted:true} snapshots).
 * A missing remote file means only "not on Drive" — e.g. another device
 * turned sync off and cleaned up — and must never delete local data. (The
 * old missing-file inference once nuked a module that was merely un-synced.)
 */
async function syncModuleCode(
  provider: SyncProvider,
  moduleId: string,
  rec: ModuleRecord,
  remoteFiles: Map<string, RemoteFile>,
  remoteContent: Map<string, string | null>,
  deviceId: string,
): Promise<CodeOutcome> {
  const remotePath = `${REMOTE_MODULES_PREFIX}/${moduleId}.json`;
  const remoteJson = remoteFiles.has(remotePath)
    ? (remoteContent.get(remotePath) ?? null)
    : null;
  const remoteSnap = remoteJson ? safeParseModuleSnap(remoteJson) : null;

  if (!remoteSnap) {
    // Remote is missing — (re-)push everything we have.
    return pushModuleCode(provider, remotePath, moduleId, rec, deviceId);
  }

  if (remoteSnap.deleted) {
    // Explicit delete marker. Local edits NEWER than the deletion mean the
    // module was recreated/changed afterwards — local wins, overwrite the
    // tombstone. Otherwise apply the deletion here too.
    const deletedAt = remoteSnap.deletedAtMs ?? remoteSnap.lastModifiedMs;
    if (rec.lastModifiedMs > deletedAt) {
      return pushModuleCode(provider, remotePath, moduleId, rec, deviceId);
    }
    storage.removeModule(moduleId);
    return 'deleted';
  }

  const remoteHtml = remoteSnap.files['index.html'] ?? '';
  const localHtml = rec.htmlSource;
  const localFiles: Record<string, string> = { ...(rec.files ?? {}), 'index.html': rec.htmlSource };

  // Compare the WHOLE file map, not just index.html — a rename or icon
  // change only touches module.json, and must still sync.
  if (sameStringMap(localFiles, remoteSnap.files)) return 'up_to_date';

  if (remoteSnap.lastModifiedMs > rec.lastModifiedMs) {
    // Pull: overwrite local with remote — including the full file map so
    // we preserve any sibling files (revisions/, module.json, …). A pulled
    // module.json may carry a rename / icon change from the other device.
    const cfg = moduleConfigFromFiles(remoteSnap.files);
    storage.putModule(moduleId, {
      ...rec,
      name: cfg.label ?? rec.name,
      icon: cfg.icon ?? rec.icon,
      htmlSource: remoteHtml,
      files: { ...remoteSnap.files },
      lastModifiedMs: remoteSnap.lastModifiedMs,
    });
    return 'downloaded';
  }

  // Local is newer (or tied). Bug-1 guard: a legacy record that never
  // materialised its file map must not win LWW and strip Android's extras
  // (revisions/, module.json) off Drive — when the code itself is identical,
  // adopt the remote extras instead of pushing.
  if (remoteHtml === localHtml && filesMapNeedsBackfill(rec.files, remoteSnap.files)) {
    storage.putModule(moduleId, {
      ...rec,
      files: { ...remoteSnap.files, ...(rec.files ?? {}), 'index.html': rec.htmlSource },
    });
    return 'up_to_date';
  }

  // Push: send local up.
  return pushModuleCode(provider, remotePath, moduleId, rec, deviceId);
}

function sameStringMap(a: Record<string, string>, b: Record<string, string>): boolean {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  for (const k of ak) if (a[k] !== b[k]) return false;
  return true;
}

/**
 * True when the remote files map carries entries that aren't reflected in
 * the local cache. We compare by key — if remote has any key local doesn't,
 * backfill (we don't bother updating individual values since the surviving
 * index.html content is by definition already in sync).
 */
function filesMapNeedsBackfill(
  local: Record<string, string> | undefined,
  remote: Record<string, string>,
): boolean {
  if (!local) return Object.keys(remote).length > 0;
  const localKeys = new Set(Object.keys(local));
  for (const k of Object.keys(remote)) {
    if (!localKeys.has(k)) return true;
  }
  return false;
}

async function pushModuleCode(
  provider: SyncProvider,
  remotePath: string,
  moduleId: string,
  rec: ModuleRecord,
  deviceId: string,
): Promise<CodeOutcome> {
  const ts = Math.max(rec.lastModifiedMs, Date.now());
  // Start from the file map we last received from sync (preserves Android's
  // revisions / module.json / etc.) and override index.html with current
  // local edits. New web-created modules with no files map yet fall back
  // to the single-file shape — Android will then own the map going forward.
  const files: Record<string, string> = { ...(rec.files ?? {}), 'index.html': rec.htmlSource };
  const snap: SyncedModule = {
    schemaVersion: SCHEMA_VERSION,
    moduleId,
    lastModifiedMs: ts,
    deviceId,
    files,
  };
  const ok = await provider.writeFile(remotePath, JSON.stringify(snap));
  if (!ok) return 'up_to_date';
  // Update local record: bump timestamp + persist the materialised map so
  // subsequent calls don't see "no map" and re-flatten.
  const patch: Partial<ModuleRecord> = { files };
  if (ts !== rec.lastModifiedMs) patch.lastModifiedMs = ts;
  storage.putModule(moduleId, { ...rec, ...patch });
  return 'uploaded';
}
